import { useState } from "react";
import StarIcon from "../../../assets/svg/star-icon";
import ChevronLeftIcon from "../../../assets/svg/chevron-left-icon";
import ChevronRightIcon from "../../../assets/svg/chevron-right-icon";
import { useScrollNavigation } from "../../../hooks/use-scroll-navigation";
import { FeatureCardData } from "../utils/types";
import { GetMoreRequestModal } from "./GetMoreRequestModal";

export const FeatureShowcaseSection = ({
  title = "Get More of our Features!",
  description = "Discover more features to boost reach, engagement, and results.",
  features = [],
  isRequestModalOpen,
  onRequestModalOpenChange,
  submitApiType = "features",
}: {
  title?: string;
  description?: string;
  features?: FeatureCardData[];
  isRequestModalOpen?: boolean;
  onRequestModalOpenChange?: (open: boolean) => void;
  submitApiType?: "modalities" | "features";
}) => {
  const [isGetMoreFeaturesModalOpen, setIsGetMoreFeaturesModalOpen] =
    useState(false);
  const {
    containerRef,
    isScrollable,
    canScrollLeft,
    canScrollRight,
    scrollTo,
    checkScrollState,
  } = useScrollNavigation(800);
  const hasFeatures = features.length > 0;
  const featuresRequestItems = features.map((item) => ({
    code: item.code,
    icon: item.icon,
    iconColor:
      "w-5 h-5 text-verba-indigo dark:text-verba-indigo-dk group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk",
    title: item.title,
    description: item.description,
    badgeText: item.badge,
    isLive: item.isLive,
  }));

  const handleOpenGetMoreFeaturesModal = () => {
    if (onRequestModalOpenChange) {
      onRequestModalOpenChange(true);
      return;
    }
    setIsGetMoreFeaturesModalOpen(true);
  };

  const handleGetMoreFeaturesModalOpenChange = (open: boolean) => {
    if (onRequestModalOpenChange) {
      onRequestModalOpenChange(open);
      return;
    }
    setIsGetMoreFeaturesModalOpen(open);
  };

  const isModalOpen = isRequestModalOpen ?? isGetMoreFeaturesModalOpen;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 pr-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <StarIcon className="w-3.5 h-3.5 text-rating-yellow dark:text-rating-yellow-dk" />
            <span className="text-base font-medium text-heading-text dark:text-heading-text-dk">
              {title}
            </span>
          </div>
          <span className="text-xs text-data dark:text-data-dk">
            {description}
          </span>
        </div>

        {isScrollable && hasFeatures && (
          <div className="flex gap-1">
            <button
              onClick={() => scrollTo("left")}
              disabled={!canScrollLeft}
              className="w-5 h-5 flex items-center justify-center rounded-[3px] text-verba-indigo dark:text-verba-indigo-dk bg-hover-effect dark:bg-hover-effect-dk disabled:bg-pure-white dark:disabled:bg-pure-white-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white dark:hover:text-fixed-white-dk disabled:hover:text-verba-indigo dark:disabled:hover:text-verba-indigo-dk disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeftIcon className="w-4 h-4 text-heading dark:text-heading-dk" />
            </button>
            <button
              onClick={() => scrollTo("right")}
              disabled={!canScrollRight}
              className="w-5 h-5 flex items-center justify-center rounded-[3px] text-verba-indigo dark:text-verba-indigo-dk bg-hover-effect dark:bg-hover-effect-dk disabled:bg-pure-white dark:disabled:bg-pure-white-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white dark:hover:text-fixed-white-dk disabled:hover:text-verba-indigo dark:disabled:hover:text-verba-indigo-dk disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <ChevronRightIcon className="w-4 h-4 text-heading dark:text-heading-dk" />
            </button>
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        onScroll={checkScrollState}
        className="no-scrollbar flex gap-3 overflow-x-auto pr-4"
      >
        {hasFeatures ? (
          features.map((item) => (
            <div
              key={item?.title}
              className="group relative w-[787px] shrink-0 rounded-lg border border-border-gray dark:border-border-gray-dk hover:bg-selected-option-highlight dark:hover:bg-selected-option-highlight-dk p-3 transition-all duration-300"
            >
              <div className="flex gap-6 items-center">
                <div className="w-[307px] h-[200px] rounded-md bg-hover-effect dark:bg-hover-effect-dk p-1 group-hover:shadow-popper-blur-effect group-hover:backdrop-blur-[1px] transition-all duration-300">
                  <img
                    src={item?.image}
                    alt={item?.title}
                    className="w-full h-full object-contain rounded-md"
                  />
                </div>

                <div className="flex flex-col flex-1">
                  <item.icon className="w-9 h-9 group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk text-verba-indigo dark:text-verba-indigo-dk" />
                  <span className="text-lg font-semibold text-verba-indigo dark:text-verba-indigo-dk mt-4">
                    {item?.title}
                  </span>
                  <span className="text-xs text-data dark:text-data-dk mt-2 mb-[18px]">
                    {item?.description}
                  </span>
                  <button
                    type="button"
                    className="hidden group-hover:block w-max px-3 py-[3px] rounded-md text-md font-semibold text-pure-white dark:text-pure-white-dk bg-verba-indigo dark:bg-verba-indigo-dk border border-border-gray dark:border-border-gray-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white dark:hover:text-fixed-white-dk transition-all duration-300"
                    onClick={handleOpenGetMoreFeaturesModal}
                  >
                    {item?.ctaText || "-"}
                  </button>
                </div>
              </div>

              {item?.badge && (
                <span className="absolute top-3 right-3 flex items-center gap-1 py-1 px-1.5 rounded text-xs font-semibold text-verba-sky dark:text-verba-sky-dk border border-border-gray dark:border-border-gray-dk group-hover:bg-verba-blue dark:group-hover:bg-verba-blue-dk group-hover:text-fixed-white dark:group-hover:text-fixed-white-dk transition-all duration-300">
                  <StarIcon className="w-3.5 h-3.5 text-rating-yellow dark:text-rating-yellow-dk" />
                  <span>{item?.badge}</span>
                </span>
              )}
            </div>
          ))
        ) : (
          <div className="w-full h-[226px] rounded-lg border border-border-gray dark:border-border-gray-dk flex items-center justify-center text-sm text-data dark:text-data-dk">
            -
          </div>
        )}
      </div>
      <GetMoreRequestModal
        open={isModalOpen}
        onOpenChange={handleGetMoreFeaturesModalOpenChange}
        title="Get More of our Features!"
        description="Get access to more of our capabilities to make your workflow simpler, faster and more effective."
        items={featuresRequestItems}
        submitApiType={submitApiType}
      />
    </div>
  );
};
